import { Fragment, useState } from "react";
import type { Project, ProjectPrintVisual } from "@portfolio/shared/project";
import {
  DocumentPage,
  DocumentPageNumber,
} from "../../components/layout/DocumentPage";
import { PageHeader } from "../../components/layout/PageHeader";
import { Reveal } from "../../components/motion/Reveal";
import { ProjectImage } from "../../components/ui/ProjectImage";
import { projects } from "../../data/projects";
import { ProjectDetailOverlay } from "./ProjectDetailOverlay";
import styles from "./Projects.module.css";

const galleryPages = [
  {
    id: "project-overview",
    pageNumber: 5,
    eyebrow: "05 / SELECTED PROJECTS",
    title: "주요 프로젝트",
    description:
      "다양한 환경에서 문제를 분석하고, 적합한 기술로 해결해 온 프로젝트입니다.",
    projectRange: [0, 10],
  },
] as const;

function ProjectCard({
  project,
  index,
  onOpen,
}: {
  project: Project;
  index: number;
  onOpen: () => void;
}) {
  return (
    <Reveal
      delay={index * 0.08}
      className={`${styles.projectCard} project-list-card`}
    >
      <div className={styles.visualMeta}>
        <span>{project.index}</span>
        <span>{project.period}</span>
      </div>

      <div className={styles.cardVisual}>
        <ProjectImage
          projectId={project.id}
          title={project.title}
          accent={project.accent}
        />
      </div>

      <div className={styles.cardBody}>
        <h3>{project.title}</h3>
        <p className={styles.cardSummary}>{project.subtitle}</p>
        <p className={styles.cardStack}>
          {project.technologies.slice(0, 5).join(" · ")}
        </p>

        <button
          type="button"
          className={styles.detailButton}
          onClick={onOpen}
        >
          자세히 보기 <span aria-hidden="true">↗</span>
        </button>
      </div>
    </Reveal>
  );
}

function ProjectGalleryPage({
  page,
  onOpen,
}: {
  page: (typeof galleryPages)[number];
  onOpen: (project: Project) => void;
}) {
  const [start, end] = page.projectRange;
  const pageProjects = projects.slice(start, end);

  return (
    <DocumentPage
      id={page.id}
      pageNumber={page.pageNumber}
      className={styles.galleryPage}
    >
      <PageHeader
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        wideDescription
      />

      <div className={styles.projectGrid}>
        {pageProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            index={index}
            onOpen={() => onOpen(project)}
          />
        ))}
      </div>
    </DocumentPage>
  );
}

function ProjectPrintDetail({
  project,
  sectionNumber,
}: {
  project: Project;
  sectionNumber: string;
}) {
  return (
    <section
      id={`project-detail-${project.id}`}
      className={`${styles.printPage} ${styles.printDetailPage} print-only`}
    >
      <div className={styles.printPageBody}>
        <header className={styles.printDetailHeader}>
        <span>{sectionNumber} / PROJECT DETAIL</span>
        <h2>{project.title}</h2>
        <p>{project.subtitle}</p>
      </header>

      <div className={styles.printDetailVisual}>
        <ProjectImage
          projectId={project.id}
          title={project.title}
          accent={project.accent}
        />
      </div>

      <dl className={styles.printProjectMeta}>
        <div>
          <dt>기간</dt>
          <dd>{project.period}</dd>
        </div>
        <div>
          <dt>역할</dt>
          <dd>{project.contribution}</dd>
        </div>
        <div>
          <dt>기술</dt>
          <dd>{project.technologies.join(" · ")}</dd>
        </div>
      </dl>

      <div className={styles.printDetailGrid}>
        <section>
          <span>01 / OVERVIEW</span>
          <h3>프로젝트 개요</h3>
          <p>{project.overview}</p>
        </section>

        <section>
          <span>02 / PROBLEM</span>
          <h3>해결해야 할 핵심 과제</h3>
          <p>{project.problem}</p>
        </section>

        <section>
          <span>03 / APPROACH</span>
          <h3>직접 담당한 핵심 기능</h3>
          <ol>
            {project.role.map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ol>
        </section>

        <section>
          <span>04 / SOLUTION</span>
          <h3>문제를 해결한 기술과 구조</h3>
          <p>{project.solution}</p>
        </section>

        <section className={styles.printResultSection}>
          <span>05 / RESULT</span>
          <h3>구현 결과와 핵심 성과</h3>

          <div className={styles.printMetrics}>
            {project.metrics.map((metric) => (
              <div key={metric.label}>
                <strong>{metric.value}</strong>
                <p>{metric.label}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
      </div>

      <DocumentPageNumber>
        {sectionNumber}
      </DocumentPageNumber>
    </section>
  );
}

function PrintProjectVisual({
  visual,
  title,
}: {
  visual: ProjectPrintVisual;
  title: string;
}) {
  return <img src={visual.src} alt={title} />;
}

function ProjectPrintImages({
  project,
  sectionNumber,
  visuals,
}: {
  project: Project;
  sectionNumber: string;
  visuals: ProjectPrintVisual[];
}) {
  if (visuals.length === 0) {
    return null;
  }

  const visibleVisuals = visuals.slice(0, 10);

  return (
    <section
      id={`project-images-${project.id}`}
      className={`${styles.printPage} ${styles.printImagesPage} print-only`}
    >
      <header className={styles.printImagesHeader}>
        <span>{sectionNumber} / PROJECT IMAGES</span>
        <h2>{project.title}</h2>
        <p>{project.subtitle}</p>
      </header>

      <div
        className={styles.printImageGrid}
        data-count={visibleVisuals.length}
      >
        {visibleVisuals.map((visual, index) => (
          <figure
            key={`${project.id}-${visual.type}-${index}`}
            className={styles.printImageItem}
          >
            <PrintProjectVisual
              visual={visual}
              title={`${project.title} 화면 ${index + 1}`}
            />
            {/* <figcaption>{String(index + 1).padStart(2, "0")}</figcaption> */}
          </figure>
        ))}
      </div>

      <DocumentPageNumber>
        {sectionNumber}
      </DocumentPageNumber>
    </section>
  );
}

interface ProjectsProps {
  suppressOverlayHint?: boolean;
}

export function Projects({
  suppressOverlayHint = false,
}: ProjectsProps) {
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  return (
    <Fragment>
      {galleryPages.map((page) => (
        <ProjectGalleryPage
          key={page.id}
          page={page}
          onOpen={setActiveProject}
        />
      ))}

      {projects.map((project, index) => {
        const sectionNumber = `05-${String(index + 1).padStart(2, "0")}`;
        const subImages = project.subImages ?? [];

        return (
          <Fragment key={`print-${project.id}`}>
            <ProjectPrintDetail
              project={project}
              sectionNumber={sectionNumber}
            />

            <ProjectPrintImages
              project={project}
              sectionNumber={sectionNumber}
              visuals={subImages}
            />
          </Fragment>
        );
      })}

      <ProjectDetailOverlay
        project={activeProject}
        onClose={() => setActiveProject(null)}
        suppressHint={suppressOverlayHint}
      />
    </Fragment>
  );
}